import banner from "../../../courses/module-2/img1.png";

const modulo1_pt = {
  courseId: "cripto-blockchain",
  id: "modulo-1",
  title: "🧩 MÓDULO 1: O que é Web3 e por que é importante?",
  estimatedDuration: "1 hora",
  format: "Leitura + atividade de verificação",
  bannerUrl: banner,
  sections: [
    {
      id: "objetivos",
      navLabel: "Objetivos",
      title: "🎯 Objetivo do módulo",
      content: [
        "Entender o que é Web3, como a internet evoluiu desde suas versões anteriores e por que essa nova fase digital pode transformar a maneira como lidamos com tecnologia, dinheiro, redes sociais e nossos próprios dados.",
        "Ao final, você saberá por que Web3 está se tornando tão importante no Brasil e no mundo.",
      ],
    },
    {
      id: "evolucion",
      navLabel: "Evolução da Web",
      title: "🔍 A evolução da Internet: da Web1 à Web3",
      content: [
        {
          type: "table",
          headers: [
            "Versão",
            "O que você podia fazer?",
            "Quem tinha o poder?",
            "Exemplos",
          ],
          rows: [
            [
              "Web 1.0 (1990–2004)",
              "Apenas ler informações",
              "Empresas ou universidades",
              "Portais, sites estáticos, páginas pessoais",
            ],
            [
              "Web 2.0 (2004–2020)",
              "Ler, postar, interagir, compartilhar",
              "Big Techs (Google, Meta, TikTok)",
              "YouTube, Instagram, Uber, iFood, Amazon",
            ],
            [
              "Web 3.0 (2020–hoje)",
              "Ler, postar e possuir informação e ativos digitais",
              "Usuários (graças ao blockchain)",
              "dApps, NFTs, DAOs, metaverso",
            ],
          ],
        },
        {
          type: "paragraph",
          text: "🧠 Resumo rápido",
        },
        {
          type: "list",
          items: [
            "Web1 era como uma biblioteca online.",
            "Web2 virou uma rede social dominada por grandes empresas.",
            "Web3 é uma nova internet onde você controla seus dados, seu dinheiro e sua identidade digital.",
          ],
        },
        {
          type: "paragraph",
          text: "🔎 Nota importante",
        },
        {
          type: "paragraph",
          text: "Você também pode ouvir sobre Web 4.0 e Web 5.0, mas esses conceitos ainda estão sendo construídos:",
        },
        {
          type: "list",
          items: [
            "Web 4.0: integração total da internet com inteligência artificial e dispositivos inteligentes (carros conectados, casas automatizadas, Alexa, Google Assistant).",
            "Web 5.0: visão futura onde a internet pode entender emoções humanas e responder de forma empática.",
          ],
        },
      ],
    },
    {
      id: "definicion",
      navLabel: "Definição",
      title: "🔍 O que é Web3?",
      content: [
        {
          type: "paragraph",
          text: "🧠 Definição simples",
        },
        {
          type: "paragraph",
          text: "Web3 é uma forma mais aberta e descentralizada de usar a internet, baseada em blockchain.",
        },
        {
          type: "paragraph",
          text: "Nela, você pode controlar seus dados, seu dinheiro e sua identidade digital, sem depender totalmente de bancos, plataformas ou empresas gigantes.",
        },
        {
          type: "paragraph",
          text: "🔑 Conceitos essenciais",
        },
        {
          type: "table",
          headers: ["Termo", "O que significa?", "Exemplo real"],
          rows: [
            [
              "Blockchain",
              "Um registro público, seguro e impossível de alterar",
              "Qualquer pessoa pode ver uma transação, mas ninguém pode apagá-la",
            ],
            [
              "Descentralização",
              "O poder não está em uma única empresa",
              "Enviar dinheiro sem banco, como no Pix, mas global e 24/7",
            ],
            [
              "Token",
              "Um ativo digital com valor registrado em blockchain",
              "Criptomoedas, NFTs, ingressos digitais",
            ],
            [
              "Wallet (carteira digital)",
              "App que guarda suas criptos e identidade digital",
              "MetaMask, Trust Wallet, Binance Wallet, Nemo",
            ],
            [
              "Criptomoeda",
              "Dinheiro digital descentralizado",
              "Bitcoin, Ethereum, Stellar (XLM)",
            ],
            [
              "dApp",
              "Aplicação sem servidor central",
              "Jogos Web3, exchanges como Uniswap",
            ],
            [
              "DAO",
              "Comunidade que toma decisões por votação",
              "Projetos onde cada pessoa vota com seus tokens",
            ],
          ],
        },
      ],
    },

    {
      id: "importante",
      navLabel: "Web3 é importante",
      title: "💡 Por que a Web3 é importante?",
      content: [
        {
          type: "list",
          items: [
            "Você ganha tokens por participar, aprender ou jogar.",
            "Tem controle real sobre sua identidade digital.",
            "Pode enviar e receber dinheiro sem bancos, inclusive internacionalmente.",
            "É possível vender conteúdo diretamente, sem depender de plataformas.",
            "O Brasil é um dos maiores mercados de cripto do mundo: há oportunidades reais de trabalho e renda.",
            "A Web3 conversa muito com o que o Brasil já vive com o Pix: transações rápidas, baratas e acessíveis.",
          ],
        },
      ],
    },
    {
      id: "casos-reales",
      navLabel: "Casos reais",
      title: "🏢 Casos reais do mundo Web3",
      content: [
        {
          type: "table",
          headers: ["Empresa / Projeto", "Como utiliza Web3?"],
          rows: [
            [
              "Reddit",
              "Permite criar avatares digitais (colecionáveis) que podem ser revendidos.",
            ],
            [
              "Nike e Adidas",
              "Vendem tênis digitais únicos com certificado blockchain.",
            ],
            [
              "Starbucks Odyssey",
              "Programa de recompensas com objetos digitais colecionáveis.",
            ],
            [
              "Brave Browser",
              "Navegador que paga usuários com tokens BAT por ver anúncios opcionais.",
            ],
            [
              "Axie Infinity",
              "Jogo onde os jogadores ganham criptomoedas enquanto jogam.",
            ],
            [
              "Mercado Livre (Brasil)",
              "Já permite comprar e vender criptomoedas dentro da plataforma.",
            ],
            [
              "Nubank Cripto",
              "Permite comprar e guardar cripto — ajudou a popularizar o tema no Brasil.",
            ],
          ],
        },
      ],
    },
    {
      id: "def",
      navLabel: "Colecionável digital",
      title: "🧠 O que é um colecionável digital?",
      content: [
        "É um item único na internet (como avatar, figurinha ou arte digital) com certificado de propriedade em blockchain.",
        "Isso é chamado de NFT, mas você pode pensar simplesmente como um ativo digital que você realmente possui.",
      ],
    },
    {
      id: "videos",
      navLabel: "Vídeos explicativos",
      title: "📺 Vídeos explicativos",
      content: [
        {
          type: "link",
          href: "https://www.youtube.com/watch?v=Dm9TvsHzoF0",
          text: "🎥 Web3: O Futuro? – Canal Criptomaníacos (YouTube)",
        },
        {
          type: "link",
          href: "https://www.youtube.com/watch?v=7avdgcSATXw",
          text: "🎥 Blockchain explicado  – Primoverso (YouTube)",
        },
        {
          type: "link",
          href: "https://www.youtube.com/watch?v=2paKViwMGts",
          text: "🎥 O que é Web3? – Universo do Bitcoin (YouTube)",
        },
      ],
    },
    {
      id: "lecturas",
      navLabel: "Leituras",
      title: "📚 Leituras complementares",
      content: [
        {
          type: "paragraph",
          text: "Binance Academy — O que é Web3?",
        },

        {
          type: "paragraph",
          text: "Mercado Bitcoin Blog — Introdução ao Web3",
        },
        {
          type: "paragraph",
          text: "CoinGecko Brasil — Glossário Web3 para iniciantes",
        },
      ],
    },
    {
      id: "actividad",
      navLabel: "Atividade",
      title: "✅ Atividade de verificação de conhecimento",
      content: [
        "Responda no seu caderno ou bloco de notas:",
        {
          type: "list",
          items: [
            "Qual a principal diferença entre Web2 e Web3?",
            "O que significa “descentralização” e por que ela importa?",
            "Para que serve uma wallet?",
            "Cite uma empresa que usa Web3 e o que você aprendeu com ela.",
            "Você usaria uma app Web3? Qual e por quê?",
          ],
        },
      ],
    },
    {
      id: "frase",
      navLabel: "Frase do dia",
      title: "🧠 Frase do dia",
      content: ["“Na Web2 você era o produto. Na Web3 você é o dono.”"],
    },
  ],
};

export default modulo1_pt;
